// features/fleet/fleetSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_V4 = "https://v4.dhundhoo.com/v2";
const API_V3 = "https://v3.dhundhoo.com";

const getHeaders = () => ({
  "x-dhundhoo-session": localStorage.getItem("token")
});

// 🔥 Helper: Remove items where latest is null/empty
const filterValidThings = (orgThings) => {
  return Object.fromEntries(
    Object.entries(orgThings || {}).filter(
      ([_, thing]) =>
        thing?.latest &&
        typeof thing.latest === "object" &&
        Object.keys(thing.latest).length > 0
    )
  );
};

// 1. Initial Load (Things + GeoFences)
export const fetchInitialFleetData = createAsyncThunk(
  'fleet/fetchInitial',
  async (_, { rejectWithValue }) => {
    try {
      const [thingsRes, geoRes] = await Promise.all([
        axios.get(`${API_V4}/things/my`, { headers: getHeaders() }),
        axios.post(`${API_V3}/thing/getGeoFence`, {}, { headers: getHeaders() })
      ]);

      return {
        orgThings: thingsRes.data.orgThings || {},
        geoFences: geoRes.data || []
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 2. Polling Updates (GPS/Speed/Ignition)
export const fetchFleetUpdates = createAsyncThunk(
  'fleet/fetchUpdates',
  async (thingIds, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_V4}/updates/latest`,
        { thingIds, loadJourneys: true },
        { headers: getHeaders() }
      );

      const updates = res.data.updates || {};

      // // 💡 Optimization: Check if any new journey started/ended
      // const anyJourneyChanged = Object.values(updates).some(
      //   (u) => u?.journeyAvailable
      // );

      // if (anyJourneyChanged) {
      //   dispatch(refreshFleetData());
      // }

      return updates;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 3. Background Refresh (Sirf updates ke waqt use hoga)
export const refreshFleetData = createAsyncThunk(
  'fleet/refreshData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_V4}/things/my`, {
        headers: getHeaders()
      });
      return res.data.orgThings || {};
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  myThings: null,
  latestUpdates: {},
  geoFences: [],
  loading: false,
  error: null,

  // UI States
  selectedVehicleNumber: null,
  activeFilter: "@total",
  searchQuery: "",
  isSidebarVisible: false,
  journeyStatusMap: {},
  shouldRefreshThings: false
};

const fleetSlice = createSlice({
  name: 'fleet',
  initialState,
reducers: {
  setSelectedVehicleNumber: (state, action) => {
    state.selectedVehicleNumber = action.payload;
  },

  setActiveFilter: (state, action) => {
    state.activeFilter = action.payload;
    if (action.payload) state.isSidebarVisible = true;
  },

  setSearchQuery: (state, action) => {
    state.searchQuery = action.payload;
  },

  setSidebarVisibility: (state, action) => {
    state.isSidebarVisible = action.payload;
  },

  // ✅ ADD HERE
  resetShouldRefreshThings: (state) => {
    state.shouldRefreshThings = false;
  },

  resetFleetState: () => initialState,
},
  extraReducers: (builder) => {
    builder

      // ✅ Initial Load
      .addCase(fetchInitialFleetData.pending, (state) => {
        if (!state.myThings) state.loading = true;
      })
      .addCase(fetchInitialFleetData.fulfilled, (state, action) => {
        state.loading = false;

        // 🔥 Filter applied here
        state.myThings = filterValidThings(action.payload.orgThings);
        state.geoFences = action.payload.geoFences;
      })
      .addCase(fetchInitialFleetData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Polling Updates
      .addCase(fetchFleetUpdates.fulfilled, (state, action) => {
        const updates = action.payload || {};

        Object.entries(updates).forEach(([thingId, update]) => {
          const currentJourney = !!update?.journeyAvailable;

          const previousJourney =
            state.journeyStatusMap?.[thingId];

          if (
            previousJourney !== undefined &&
            previousJourney !== currentJourney
          ) {
            state.shouldRefreshThings = true;
          }

          state.journeyStatusMap[thingId] = currentJourney;
        });

        state.latestUpdates = {
          ...state.latestUpdates,
          ...updates
        };
      })
      .addCase(fetchFleetUpdates.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ✅ Background Refresh
      .addCase(refreshFleetData.fulfilled, (state, action) => {
        // 🔥 Filter applied here also
        state.myThings = filterValidThings(action.payload);
      })
      .addCase(refreshFleetData.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const {
  setSelectedVehicleNumber,
  setActiveFilter,
  setSearchQuery,
  setSidebarVisibility,
  resetShouldRefreshThings,
  resetFleetState
} = fleetSlice.actions;

export default fleetSlice.reducer;